import { ACHIEVEMENT_BY_ID } from "@nano/content";
import { useEffect, useMemo, useRef, useState } from "react";
import { claimAchievement, closeOverlay, dismissAchievementPopup } from "../../app/actions.ts";
import { shallowEqual, useApp } from "../../app/store.ts";
import { achievementDescription, achievementName } from "../../i18n/content.ts";
import { vi } from "../../i18n/vi.ts";
import { achievementViews, type AchievementView } from "../../services/achievements.ts";
import { Art, Coins, Sheet } from "../components/common.tsx";
import { centerOf, fx } from "../fx/fx.ts";

const ORDER: Record<AchievementView["status"], number> = { claimable: 0, locked: 1, earned: 2, claimed: 3 };

/** Achievement list: claim gold (server-verified), see progress, admire badges. */
export function Achievements() {
  const { save, online } = useApp((s) => ({ save: s.save, online: s.ui.online }), shallowEqual);
  const views = useMemo(() => (save ? achievementViews(save) : []), [save]);
  const [busy, setBusy] = useState<string | null>(null);
  const coinsRef = useRef<HTMLDivElement>(null);
  if (!save) return null;

  // Each family shows its rewards waiting to be claimed plus its next goal,
  // or its top tier once everything is done, so the list stays short.
  const families = new Map<string, AchievementView[]>();
  for (const view of views) families.set(view.def.family, [...(families.get(view.def.family) ?? []), view]);
  const unique: AchievementView[] = [];
  for (const family of families.values()) {
    const claimable = family.filter((view) => view.status === "claimable");
    const next = family.find((view) => view.status === "locked");
    unique.push(...claimable);
    if (next) unique.push(next);
    else if (claimable.length === 0 && family.length > 0) unique.push(family[family.length - 1] as AchievementView);
  }
  unique.sort((a, b) => ORDER[a.status] - ORDER[b.status]);
  const done = views.filter((view) => view.status !== "locked").length;
  const gold = save.server?.wallet.gold ?? 0;

  const claim = async (view: AchievementView, element: HTMLElement) => {
    setBusy(view.def.id);
    const paid = await claimAchievement(view.def.id);
    setBusy(null);
    if (paid && paid > 0) {
      const from = centerOf(element);
      const to = centerOf(coinsRef.current);
      if (from) fx.sparkles(from.x, from.y, 14, 140);
      if (from && to) fx.coins(from, to, Math.ceil(paid / 10));
    }
  };

  return (
    <Sheet title={vi.achievements.title} onClose={() => closeOverlay()} className="achievements" testId="achievements">
      <div className="achievements__head">
        <span className="note">{vi.achievements.summary(done, views.length)}</span>
        <div ref={coinsRef}>
          <Coins amount={gold} />
        </div>
      </div>
      {!online ? <p className="note">{vi.achievements.offline}</p> : null}
      <ul className="achievements__list">
        {unique.map((view) => (
          <AchievementRow key={view.def.id} view={view} busy={busy === view.def.id} canClaim={online} onClaim={claim} />
        ))}
      </ul>
      <p className="note">{vi.achievements.deviceNote}</p>
    </Sheet>
  );
}

function AchievementRow({
  view,
  busy,
  canClaim,
  onClaim,
}: {
  view: AchievementView;
  busy: boolean;
  canClaim: boolean;
  onClaim: (view: AchievementView, element: HTMLElement) => void;
}) {
  const { def, status, current } = view;
  const pct = Math.round((current / def.target) * 100);
  return (
    <li className={`ach ach--${status}`} data-testid={`ach-${def.id}`}>
      <span className="ach__badge">
        <Art id={def.badgeAssetId} className="ach__art" />
        {def.tierCount > 1 ? <span className="ach__tier">{def.tier}</span> : null}
      </span>
      <span className="ach__text">
        <strong className="ach__name">{achievementName(def)}</strong>
        <span className="ach__desc">{achievementDescription(def)}</span>
        {status === "locked" ? (
          <span className="ach__bar" aria-label={vi.achievements.progress(current, def.target)}>
            <span style={{ width: `${pct}%` }} />
            <em>{vi.achievements.progress(current, def.target)}</em>
          </span>
        ) : null}
      </span>
      <span className="ach__side">
        {status === "claimable" ? (
          <button
            type="button"
            className="ach__claim"
            disabled={busy || !canClaim}
            onClick={(event) => onClaim(view, event.currentTarget)}
            data-testid={`claim-${def.id}`}
          >
            <Art id="reward.coin" className="ach__coin" />
            {vi.achievements.claim(def.rewardGold)}
          </button>
        ) : status === "claimed" ? (
          <span className="ach__done">
            <Art id="icon.check" className="ach__check" />
            {vi.achievements.claimed}
          </span>
        ) : status === "earned" ? (
          <span className="ach__done">{vi.achievements.earned}</span>
        ) : def.rewardGold > 0 ? (
          <span className="ach__reward">
            <Art id="reward.coin" className="ach__coin" />
            {def.rewardGold}
          </span>
        ) : (
          <span className="ach__reward ach__reward--badge">{vi.achievements.badgeOnly}</span>
        )}
      </span>
    </li>
  );
}

/** Slides in from the top when something is unlocked; tap to open the list. */
export function AchievementPopup() {
  const popup = useApp((s) => s.ui.achievementPopup);
  const badgeRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!popup) return;
    const burst = setTimeout(() => {
      const at = centerOf(badgeRef.current);
      if (at) fx.sparkles(at.x, at.y, 16, 120);
    }, 380);
    const hide = setTimeout(() => dismissAchievementPopup(), 4800);
    return () => {
      clearTimeout(burst);
      clearTimeout(hide);
    };
  }, [popup]);
  if (!popup) return null;
  const defs = popup.ids.map((id) => ACHIEVEMENT_BY_ID.get(id)).filter((def) => def !== undefined);
  const first = defs[0];
  if (!first) return null;
  const gold = defs.reduce((sum, def) => sum + def.rewardGold, 0);
  return (
    <button type="button" className="ach-pop" key={popup.key} onClick={() => dismissAchievementPopup(true)} data-testid="achievement-popup">
      <span className="ach-pop__badge" ref={badgeRef}>
        <span className="ach-pop__shine" />
        <Art id={first.badgeAssetId} className="ach-pop__art" />
      </span>
      <span className="ach-pop__text">
        <small>{defs.length > 1 ? vi.achievements.unlockedMany(defs.length) : vi.achievements.unlocked}</small>
        <strong>{defs.length > 1 ? defs.map(achievementName).join(", ") : achievementName(first)}</strong>
        {gold > 0 ? (
          <span className="ach-pop__gold">
            <Art id="reward.coin" className="ach__coin" /> {vi.achievements.rewardLine(gold)}
          </span>
        ) : null}
      </span>
    </button>
  );
}
